import type { ReactNode } from 'react'

type IconProps = { className?: string }

const defaults = {
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  'aria-hidden': true,
}

function Icon({
  className = 'h-4 w-4 shrink-0',
  children,
}: IconProps & { children: ReactNode }) {
  return (
    <svg className={className} {...defaults}>
      {children}
    </svg>
  )
}

export function IconSearch({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M16.25 16.25 20 20"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconClear({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M6.75 6.75 17.25 17.25M17.25 6.75 6.75 17.25"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconSave({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M6.75 3.75h9.5l3 3v12.5a1 1 0 0 1-1 1H5.75a1 1 0 0 1-1-1V4.75a1 1 0 0 1 1-1h1ZM8 3.75v5.5h8v-5.5M8.5 20.25v-6h7v6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconPlus({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconEye({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M2.25 12s3.75-7.5 9.75-7.5S21.75 12 21.75 12s-3.75 7.5-9.75 7.5S2.25 12 2.25 12Z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <circle cx="12" cy="12" r="2.75" stroke="currentColor" strokeWidth="1.75" />
    </Icon>
  )
}

export function IconRefresh({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66M20 4v6h-6"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconReset({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M4 7V4h3M4 16v3h3M20 7V4h-3M20 16v3h-3M7 12h10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconClose({ className }: IconProps) {
  return <IconClear className={className} />
}

export function IconChevronLeft({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M14.25 6.75 9 12l5.25 5.25"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconChevronRight({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M9.75 6.75 15 12l-5.25 5.25"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconLogin({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M15 12H4.75M12 8.25 15.5 12 12 15.75M9.75 4.75h7.5a1 1 0 0 1 1 1v12.5a1 1 0 0 1-1 1h-7.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconLogout({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 12h8.25M15.75 8.25 19.5 12l-3.75 3.75M15.75 15.75 19.5 12l-3.75-3.75"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconExternalLink({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M14.25 4.75h4.5v4.5M10 14 18.75 9.25M18.25 5.75 10.5 5.75H5.75v12.5h12.5v-7.75"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconCalendarPlus({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M8 4.75v2M16 4.75v2M5.75 9.25h12.5M6.75 6.75h10.5a1 1 0 0 1 1 1v10.5a1 1 0 0 1-1 1H6.75a1 1 0 0 1-1-1V7.75a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
      <path
        d="M12 12v4M10 14h4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconLayers({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M12 4.75 4.75 9 12 13.25 19.25 9 12 4.75ZM4.75 14 12 18.25 19.25 14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconBan({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M7.5 7.5 16.5 16.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconCheckCircle({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M8.25 12.25 10.75 14.75 15.75 9.25"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconEdit({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}

export function IconSparkles({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.75"
      />
    </Icon>
  )
}
